import { useMutation } from "@apollo/client";
import { withJsonFormsMultiEnumProps } from "@jsonforms/react";
import { ButtonGroup, Dropdown, Modal, TextInput } from "pepsico-ds";
import { useContext, useEffect, useState } from "react";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import saveTag from "../../../graphql/mutations/saveTag";
import Button from "../../common/button/Button";
import ToastNotification from "../../common/toastNotification/ToastNotification";
import "./_campaign-tag-select.scss";

const CampaignTagSelectComponent = (props) => {
  const { handleChange, label, path, required, visible, enabled, uischema, data } =
    props;

  const formatOptions = (options) =>
    options.map((option) => ({ id: option, displayText: option }));
  const formatData = (data) => (data ? data.map((item) => item.id) : []);

  const [selected, setSelected] = useState(data?.length ? formatOptions(data) : []);
  const [openModal, setOpenModal] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [saveTagMutation, { loading }] = useMutation(saveTag);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState({
    open: false,
    type: "",
    title: "",
    description: "",
  });

  const { tagsData, refetchProgramConfig } = useContext(GlobalConfigContext);
  const [tagsOptions, setTagsOptions] = useState(
    tagsData?.[uischema.tagType?.toLowerCase() || "campaign"] || []
  );

  useEffect(() => {
    if (data?.length > 0 && selected?.length === 0) {
      setSelected(formatOptions(data));
    }
  }, [data, selected?.length]);

  const handleSelectValue = (val) => {
    setSelected(val);
    handleChange(path, formatData(val));
  };

  const handleToggleModal = () => {
    setOpenModal((prev) => !prev);
    resetNewTag();
  };

  const handleTypeTag = (e) => {
    setNewTag(e.target.value.trimStart());
    setErrorMessage("");
  };

  const checkIsUniqueTag = (newTag) => {
    const isTagNameValid = tagsOptions.every((tag) => tag !== newTag);
    return isTagNameValid;
  };

  const handleSaveTag = async () => {
    try {
      const isTagUnique = checkIsUniqueTag(newTag);
      if (!isTagUnique) {
        setErrorMessage("Tag name is already in use.");
        return;
      }
      const params = {
        variables: {
          tag: {
            type: uischema.tagType || "CAMPAIGN",
            value: newTag,
          },
        },
      };
      const updatedTags = await saveTagMutation(params);
      if (updatedTags?.data?.saveTag) {
        handleTagSaveSuccess(updatedTags.data.saveTag);
      }
    } catch {
      handleTagSaveError();
    }
  };

  const handleTagSaveSuccess = (updatedTags) => {
    setTagsOptions(updatedTags);
    refetchProgramConfig();
    openSuccessToast(newTag);
    resetNewTag();
    handleToggleModal();
  };

  const handleTagSaveError = () => {
    openErrorToast();
    resetNewTag();
    handleToggleModal();
  };

  const resetNewTag = () => setNewTag("");

  const openSuccessToast = (createdTag) => {
    setToast({
      open: true,
      type: "success",
      title: `'${createdTag}' Created!`,
      description: "You have just created a new tag!",
    });
  };

  const openErrorToast = () => {
    setToast({
      open: true,
      type: "error",
      title: "Something went wrong while saving the tag.",
      description: "Try again later.",
    });
  };

  const createTagButtonProps = {
    label: "Create New Tag",
    onClick: handleToggleModal,
  };

  return visible ? (
    <div className="tag-select">
      <Dropdown
        label={label}
        childList={formatOptions(tagsOptions)}
        selection="multiple"
        isRequired={required}
        disabled={!enabled}
        selectedValue={selected}
        setSelectedValue={handleSelectValue}
      />
      <ButtonGroup items={[createTagButtonProps]} />
      {openModal && (
        <Modal
          className="create-tag-modal"
          showModal={openModal}
          title="Create a New Tag"
          text=""
          showTertiaryButton={false}
          showLink={false}
          showSecondaryButton={false}
          primaryButtonProps={{}}
          onCloseModal={handleToggleModal}
        >
          <TextInput
            placeholder="Type here"
            maxLength={uischema.maxLength}
            value={newTag}
            onChange={handleTypeTag}
            error={errorMessage}
            style={{ width: "100%" }}
            placeholderText="enter tag name"
          />
          <div className="action-buttons">
            <Button
              size="medium"
              isLoading={loading}
              onClick={handleSaveTag}
              disabled={!newTag.length}
            >
              Create
            </Button>
          </div>
        </Modal>
      )}
      <ToastNotification
        {...toast}
        handleClose={() => setToast({ ...toast, open: false })}
      />
    </div>
  ) : null;
};

export const CampaignTagSelectControl = withJsonFormsMultiEnumProps(
  CampaignTagSelectComponent
);
